import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config';

// Types for CMS content
export interface CMSPage {
  id: string;
  title: string;
  slug: string;
  metaDescription?: string;
  metaKeywords?: string;
  status: 'draft' | 'published' | 'archived';
  sections: CMSSection[];
  template: string; // Changed to string to support custom templates
  featuredImage?: string;
  publishedAt?: number;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  updatedBy: string;
}

export interface CMSSection {
  id: string;
  type: 'hero' | 'text' | 'image_text' | 'gallery' | 'services' | 'team' | 'timeline' | 'contact_info' | 'cta' | 'custom';
  title?: string;
  content?: string; // HTML content
  settings: Record<string, any>;
  order: number;
  isVisible: boolean;
}

export interface CMSNavigation {
  id: string;
  label: string;
  url: string;
  order: number;
  isExternal: boolean;
  isVisible: boolean;
  parentId?: string;
  children?: CMSNavigation[];
}

export interface CMSSiteSettings {
  id: string;
  siteName: string;
  siteDescription: string;
  siteKeywords: string;
  logo?: string;
  favicon?: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  };
  businessHours: {
    [key: string]: {
      open: string;
      close: string;
      closed: boolean;
    };
  };
  updatedAt: number;
  updatedBy: string;
}

export interface CMSContent {
  id: string;
  key: string; // Unique identifier for content blocks
  title: string;
  content: string;
  type: 'text' | 'html' | 'image' | 'video' | 'json';
  category?: string;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  updatedBy: string;
}

// New interfaces for template management
export interface CMSTemplate {
  id: string;
  name: string;
  description?: string;
  category: 'built-in' | 'custom';
  isSystem: boolean; // Cannot be deleted if true
  thumbnail?: string;
  sections: CMSSection[];
  layoutSettings: TemplateLayoutSettings;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  updatedBy: string;
}

export interface TemplateLayoutSettings {
  containerMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  spacing: number; // Default spacing between sections
  backgroundColor?: string;
  padding?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  headerSettings?: {
    showHeader: boolean;
    headerHeight?: number;
    headerBackgroundColor?: string;
    headerTextColor?: string;
  };
  footerSettings?: {
    showFooter: boolean;
    footerBackgroundColor?: string;
    footerTextColor?: string;
  };
}

export interface SectionLayoutOptions {
  backgroundColor?: string;
  textColor?: string;
  padding?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  margin?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  borderRadius?: number;
  boxShadow?: string;
  backgroundImage?: string;
  backgroundSize?: 'cover' | 'contain' | 'auto';
  backgroundPosition?: string;
  overlayColor?: string;
  overlayOpacity?: number;
}

// Collection references
const pagesRef = collection(db, 'cms_pages');
const navigationRef = collection(db, 'cms_navigation');
const siteSettingsRef = collection(db, 'cms_site_settings');
const contentRef = collection(db, 'cms_content');
const templatesRef = collection(db, 'cms_templates');

// Page management functions
export const createPage = async (pageData: Omit<CMSPage, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = Date.now();
  const page: Omit<CMSPage, 'id'> = {
    ...pageData,
    createdAt: now,
    updatedAt: now,
  };
  
  const docRef = doc(pagesRef);
  await setDoc(docRef, page);
  return docRef.id;
};

export const updatePage = async (pageId: string, updates: Partial<CMSPage>): Promise<void> => {
  const pageRef = doc(pagesRef, pageId);
  await updateDoc(pageRef, {
    ...updates,
    updatedAt: Date.now(),
  });
};

export const getPage = async (pageId: string): Promise<CMSPage | null> => {
  const pageRef = doc(pagesRef, pageId);
  const pageSnap = await getDoc(pageRef);
  
  if (pageSnap.exists()) {
    return { ...pageSnap.data(), id: pageSnap.id } as CMSPage;
  }
  return null;
};

export const getPageBySlug = async (slug: string): Promise<CMSPage | null> => {
  const q = query(pagesRef, where('slug', '==', slug), where('status', '==', 'published'), limit(1));
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    return { ...doc.data(), id: doc.id } as CMSPage;
  }
  return null;
};

export const getPages = async (
  status?: 'draft' | 'published' | 'archived',
  pageSize: number = 20,
  lastVisible?: DocumentSnapshot
): Promise<{ pages: CMSPage[]; lastVisible: DocumentSnapshot | null }> => {
  let q = query(pagesRef, orderBy('updatedAt', 'desc'), limit(pageSize));
  
  if (status) {
    q = query(pagesRef, where('status', '==', status), orderBy('updatedAt', 'desc'), limit(pageSize));
  }
  
  if (lastVisible) {
    q = query(q, startAfter(lastVisible));
  }
  
  const querySnapshot = await getDocs(q);
  const pages = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as CMSPage));
  const newLastVisible = querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null;
  
  return { pages, lastVisible: newLastVisible };
};

export const deletePage = async (pageId: string): Promise<void> => {
  const pageRef = doc(pagesRef, pageId);
  await deleteDoc(pageRef);
};

// Navigation management functions
export const saveNavigation = async (navigation: Omit<CMSNavigation, 'id'>[]): Promise<void> => {
  // Clear existing navigation
  const existingNav = await getDocs(navigationRef);
  const batch = writeBatch(db);
  
  existingNav.docs.forEach(doc => {
    batch.delete(doc.ref);
  });
  
  // Add new navigation items
  navigation.forEach(item => {
    const docRef = doc(navigationRef);
    batch.set(docRef, item);
  });
  
  await batch.commit();
};

export const getNavigation = async (): Promise<CMSNavigation[]> => {
  const q = query(navigationRef, orderBy('order'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as CMSNavigation));
};

// Site settings functions
export const getSiteSettings = async (): Promise<CMSSiteSettings | null> => {
  const settingsRef = doc(siteSettingsRef, 'main');
  const settingsSnap = await getDoc(settingsRef);
  
  if (settingsSnap.exists()) {
    return { ...settingsSnap.data(), id: settingsSnap.id } as CMSSiteSettings;
  }
  return null;
};

export const updateSiteSettings = async (settings: Partial<CMSSiteSettings>, updatedBy: string): Promise<void> => {
  const settingsRef = doc(siteSettingsRef, 'main');
  await setDoc(settingsRef, {
    ...settings,
    updatedAt: Date.now(),
    updatedBy,
  }, { merge: true });
};

// Content blocks management
export const createContent = async (contentData: Omit<CMSContent, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = Date.now();
  const content: Omit<CMSContent, 'id'> = {
    ...contentData,
    createdAt: now,
    updatedAt: now,
  };
  
  const docRef = doc(contentRef);
  await setDoc(docRef, content);
  return docRef.id;
};

export const updateContent = async (contentId: string, updates: Partial<CMSContent>): Promise<void> => {
  const contentDocRef = doc(contentRef, contentId);
  await updateDoc(contentDocRef, {
    ...updates,
    updatedAt: Date.now(),
  });
};

export const getContent = async (key: string): Promise<CMSContent | null> => {
  const q = query(contentRef, where('key', '==', key), where('isActive', '==', true), limit(1));
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    return { ...doc.data(), id: doc.id } as CMSContent;
  }
  return null;
};

export const getAllContent = async (category?: string): Promise<CMSContent[]> => {
  let q = query(contentRef, orderBy('title'));
  
  if (category) {
    q = query(contentRef, where('category', '==', category), orderBy('title'));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as CMSContent));
};

export const deleteContent = async (contentId: string): Promise<void> => {
  const contentDocRef = doc(contentRef, contentId);
  await deleteDoc(contentDocRef);
};

// Template functions for common page structures
export const createDefaultPageTemplate = (): CMSSection[] => [
  {
    id: 'hero',
    type: 'hero',
    title: 'Hero Section',
    settings: {
      backgroundImage: '',
      overlayOpacity: 0.4,
      height: '400px'
    },
    order: 1,
    isVisible: true,
  },
  {
    id: 'content',
    type: 'text',
    title: 'Main Content',
    content: '<p>Enter your page content here...</p>',
    settings: {},
    order: 2,
    isVisible: true,
  },
];

export const createAboutPageTemplate = (): CMSSection[] => [
  {
    id: 'hero',
    type: 'hero',
    title: 'Hero Section',
    settings: {
      backgroundImage: '',
      overlayOpacity: 0.4,
      height: '400px'
    },
    order: 1,
    isVisible: true,
  },
  {
    id: 'story',
    type: 'image_text',
    title: 'Our Story',
    content: '<h2>Our Story</h2><p>Tell your story here...</p>',
    settings: {
      imagePosition: 'left',
      imageUrl: ''
    },
    order: 2,
    isVisible: true,
  },
  {
    id: 'timeline',
    type: 'timeline',
    title: 'Our Journey',
    settings: {
      items: []
    },
    order: 3,
    isVisible: true,
  },
  {
    id: 'team',
    type: 'team',
    title: 'Meet Our Team',
    settings: {
      members: []
    },
    order: 4,
    isVisible: true,
  },
];

// Utility functions
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export const validatePageData = (page: Partial<CMSPage>): string[] => {
  const errors: string[] = [];
  
  if (!page.title?.trim()) {
    errors.push('Title is required');
  }
  
  if (!page.slug?.trim()) {
    errors.push('Slug is required');
  }
  
  if (page.slug && !/^[a-z0-9-]+$/.test(page.slug)) {
    errors.push('Slug can only contain lowercase letters, numbers, and hyphens');
  }
  
  return errors;
};

// Template management functions
export const createTemplate = async (templateData: Omit<CMSTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const now = Date.now();
  const template: Omit<CMSTemplate, 'id'> = {
    ...templateData,
    createdAt: now,
    updatedAt: now,
  };
  
  const docRef = doc(templatesRef);
  await setDoc(docRef, template);
  return docRef.id;
};

export const updateTemplate = async (templateId: string, updates: Partial<CMSTemplate>): Promise<void> => {
  const templateRef = doc(templatesRef, templateId);
  await updateDoc(templateRef, {
    ...updates,
    updatedAt: Date.now(),
  });
};

export const getTemplate = async (templateId: string): Promise<CMSTemplate | null> => {
  const templateRef = doc(templatesRef, templateId);
  const templateSnap = await getDoc(templateRef);
  
  if (templateSnap.exists()) {
    return { ...templateSnap.data(), id: templateSnap.id } as CMSTemplate;
  }
  return null;
};

export const getTemplates = async (category?: 'built-in' | 'custom'): Promise<CMSTemplate[]> => {
  let q = query(templatesRef, orderBy('name'));
  
  if (category) {
    q = query(templatesRef, where('category', '==', category), orderBy('name'));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as CMSTemplate));
};

export const deleteTemplate = async (templateId: string): Promise<void> => {
  const template = await getTemplate(templateId);
  if (template?.isSystem) {
    throw new Error('Cannot delete system templates');
  }
  
  const templateRef = doc(templatesRef, templateId);
  await deleteDoc(templateRef);
};

export const duplicateTemplate = async (templateId: string, newName: string, userId: string): Promise<string> => {
  const originalTemplate = await getTemplate(templateId);
  if (!originalTemplate) {
    throw new Error('Template not found');
  }
  
  const duplicatedTemplate: Omit<CMSTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
    name: newName,
    description: `Copy of ${originalTemplate.name}`,
    category: 'custom',
    isSystem: false,
    sections: originalTemplate.sections.map(section => ({
      ...section,
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    })),
    layoutSettings: { ...originalTemplate.layoutSettings },
    createdBy: userId,
    updatedBy: userId,
  };
  
  return await createTemplate(duplicatedTemplate);
};

// Template helper functions
export const createTemplateFromPage = async (page: CMSPage, templateName: string, userId: string): Promise<string> => {
  const template: Omit<CMSTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
    name: templateName,
    description: `Template created from page: ${page.title}`,
    category: 'custom',
    isSystem: false,
    sections: page.sections.map(section => ({
      ...section,
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content: section.content || '', // Remove specific content for template
    })),
    layoutSettings: {
      containerMaxWidth: 'lg',
      spacing: 4,
      padding: { top: 2, bottom: 2, left: 2, right: 2 },
      headerSettings: { showHeader: true },
      footerSettings: { showFooter: true },
    },
    createdBy: userId,
    updatedBy: userId,
  };
  
  return await createTemplate(template);
};

export const applyTemplateToPage = (template: CMSTemplate, pageId?: string): Partial<CMSPage> => {
  return {
    template: template.id,
    sections: template.sections.map(section => ({
      ...section,
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    })),
  };
};

// Built-in template creation functions (updated to use new system)
export const initializeBuiltInTemplates = async (userId: string): Promise<void> => {
  const builtInTemplates = [
    {
      name: 'Default Page',
      description: 'A simple page with hero section and content area',
      category: 'built-in' as const,
      isSystem: true,
      sections: createDefaultPageTemplate(),
      layoutSettings: {
        containerMaxWidth: 'lg' as const,
        spacing: 4,
        padding: { top: 2, bottom: 2, left: 2, right: 2 },
        headerSettings: { showHeader: true },
        footerSettings: { showFooter: true },
      },
      createdBy: userId,
      updatedBy: userId,
    },
    {
      name: 'About Page',
      description: 'Perfect for telling your story with timeline and team sections',
      category: 'built-in' as const,
      isSystem: true,
      sections: createAboutPageTemplate(),
      layoutSettings: {
        containerMaxWidth: 'lg' as const,
        spacing: 6,
        padding: { top: 3, bottom: 3, left: 2, right: 2 },
        headerSettings: { showHeader: true },
        footerSettings: { showFooter: true },
      },
      createdBy: userId,
      updatedBy: userId,
    },
    {
      name: 'Landing Page',
      description: 'High-converting landing page with CTA sections',
      category: 'built-in' as const,
      isSystem: true,
      sections: createLandingPageTemplate(),
      layoutSettings: {
        containerMaxWidth: 'xl' as const,
        spacing: 0,
        padding: { top: 0, bottom: 0, left: 0, right: 0 },
        headerSettings: { showHeader: false },
        footerSettings: { showFooter: true },
      },
      createdBy: userId,
      updatedBy: userId,
    },
    {
      name: 'Contact Page',
      description: 'Contact form with location and business hours',
      category: 'built-in' as const,
      isSystem: true,
      sections: createContactPageTemplate(),
      layoutSettings: {
        containerMaxWidth: 'md' as const,
        spacing: 4,
        padding: { top: 4, bottom: 4, left: 2, right: 2 },
        headerSettings: { showHeader: true },
        footerSettings: { showFooter: true },
      },
      createdBy: userId,
      updatedBy: userId,
    },
  ];

  // Check if templates already exist to avoid duplicates
  const existingTemplates = await getTemplates('built-in');
  const existingNames = existingTemplates.map(t => t.name);

  for (const template of builtInTemplates) {
    if (!existingNames.includes(template.name)) {
      await createTemplate(template);
    }
  }
};

export const createLandingPageTemplate = (): CMSSection[] => [
  {
    id: 'hero',
    type: 'hero',
    title: 'Hero Section',
    content: '<h1>Welcome to Our Amazing Service</h1><p>Transform your business with our innovative solutions</p>',
    settings: {
      backgroundImage: '',
      overlayOpacity: 0.6,
      height: '600px',
      textAlign: 'center',
      showCTA: true,
      ctaText: 'Get Started',
      ctaLink: '#contact',
    },
    order: 1,
    isVisible: true,
  },
  {
    id: 'features',
    type: 'services',
    title: 'Key Features',
    settings: {
      columns: 3,
      showIcons: true,
      backgroundColor: '#f8f9fa',
    },
    order: 2,
    isVisible: true,
  },
  {
    id: 'cta',
    type: 'cta',
    title: 'Ready to Get Started?',
    content: '<p>Join thousands of satisfied customers</p>',
    settings: {
      backgroundColor: '#007bff',
      textColor: 'white',
      buttonText: 'Start Free Trial',
      buttonLink: '#signup',
    },
    order: 3,
    isVisible: true,
  },
];

export const createContactPageTemplate = (): CMSSection[] => [
  {
    id: 'hero',
    type: 'hero',
    title: 'Contact Us',
    content: '<h1>Get in Touch</h1><p>We\'d love to hear from you</p>',
    settings: {
      height: '300px',
      textAlign: 'center',
    },
    order: 1,
    isVisible: true,
  },
  {
    id: 'contact_info',
    type: 'contact_info',
    title: 'Contact Information',
    settings: {
      showMap: true,
      showHours: true,
      showForm: true,
    },
    order: 2,
    isVisible: true,
  },
]; 